import { type FC, useEffect } from "react";
import { type ConnectedProps, connect } from "react-redux";
import { Icon, type SemanticICONS } from "semantic-ui-react";

import { type ColumnOption } from "../../../../interfaces/columnOptions";
import { bindAction } from "../../../../interfaces/redux";
import dateTimeUtils from "../../../../utils/dateTimeUtils";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import { selectEngagementPeopleData, selectEngagementPeopleValue } from "../selectors/performanceSelectors";
import { fetchEngagementPeopleData } from "../state/thunks/pdfPerformanceThunk";
import { type EngagementPeople, EngagementStatus } from "../types/state";

import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import {
  isUserDeleted,
  formatUserName,
  type PerformanceWithAccountFilter,
  canUseChildAccount,
} from "features/Library/Common/utils/performanceUtils";
import { GenericPerformanceList } from "features/Reporting/Content/shared/GenericPerformanceList";
import { EMPTY_CELL_CONTENT } from "../../../../utils/columnUtils";
import { TextTruncate } from "components";
import { setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { RStatus } from "features/Application/globaltypes/fetchRequest";
import { Tooltip } from "components/common/tooltip";
import { RouteNames } from "enums";

import styles from "./PdfPerformanceBody.module.scss";
import { MoboLink } from "components/MoboLink/MoboLink";

export type PdfPerformancePeopleProps = {
  pdfId: number;
  filter: PerformanceWithAccountFilter;
  includeAccounts?: boolean;
  flowId?: number;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
} & PropsFromRedux;

export type EmailStatusColors = "icon-send-complete" | "icon-send-progress" | "icon-send-canceled";

const StatusToString: { [key in EngagementStatus]: string } = {
  [EngagementStatus.Completed]: "Completed",
  [EngagementStatus.NotStarted]: "Not Started",
};

const StatusToIcon: { [key in EngagementStatus]: SemanticICONS } = {
  [EngagementStatus.Completed]: "check circle",
  [EngagementStatus.NotStarted]: "circle outline",
};

const StatusToColor: { [key in EngagementStatus]: EmailStatusColors } = {
  [EngagementStatus.Completed]: "icon-send-complete",
  [EngagementStatus.NotStarted]: "icon-send-canceled",
};

const getColumnOptions = (
  includeAccounts: boolean = false,
  childAccountId?: number,
  hasCreateAccountsAddOn?: boolean,
) => {
  const baseColumnOptions: ColumnOption<EngagementPeople>[] = [
    {
      name: "NAME",
      width: 5,
      isSortable: false,
      render: (entity: EngagementPeople) => {
        const secondaryInfo = [entity.UserTitle, entity.UserDepartment].filter((x) => x).join(", ");
        const formattedNameDisplay = formatUserName(entity.UserFirstName, entity.UserLastName);
        const childAccountEnabled = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);
        const shouldShowLink =
          !isUserDeleted(entity.UserFirstName, entity.UserLastName) && (!includeAccounts || childAccountEnabled);
        return (
          <div>
            {shouldShowLink ? (
              <MoboLink
                to={`/${RouteNames.peopleUsers}/${entity.UserId}`}
                className={styles.linkButton}
                accountId={childAccountEnabled ? childAccountId : undefined}
                target={childAccountEnabled ? "_blank" : undefined}
              >
                <Tooltip target={<TextTruncate>{formattedNameDisplay}</TextTruncate>} content={formattedNameDisplay} />
              </MoboLink>
            ) : (
              <Tooltip target={<TextTruncate>{formattedNameDisplay}</TextTruncate>} content={formattedNameDisplay} />
            )}
            <div className={styles.department}>
              <Tooltip target={<TextTruncate>{secondaryInfo}</TextTruncate>} content={secondaryInfo} />
            </div>
          </div>
        );
      },
    },
    {
      name: "EMAIL",
      width: 5,
      isSortable: false,
      render: (entity: EngagementPeople) => (
        <Tooltip target={<TextTruncate>{entity.UserEmail}</TextTruncate>} content={`${entity.UserEmail}`} />
      ),
    },
    {
      name: "DATE VIEWED",
      width: 4,
      isSortable: false,
      render: (entity: EngagementPeople) =>
        entity.DateWatched ? dateTimeUtils.formatDate(entity.DateWatched) : EMPTY_CELL_CONTENT,
    },
    {
      name: "STATUS",
      width: 3,
      isSortable: false,
      render: (entity: EngagementPeople) => (
        <div className={styles.status}>
          <Icon name={StatusToIcon[entity.Status]} className={`${StatusToColor[entity.Status]} ${styles.icon}`} />
          {StatusToString[entity.Status]}
        </div>
      ),
    },
  ];

  return baseColumnOptions;
};

export const PdfPerformancePeople: FC<PdfPerformancePeopleProps> = ({
  fetchEngagementPeopleData,
  pdfId,
  includeAccounts,
  childAccountId,
  hasCreateAccountsAddOn,
  peopleDetailsEntities,
  filter,
  flowId,
  peopleDetailsEntitiesStatus,
  setHasExportData,
}) => {
  const columnOptions = getColumnOptions(includeAccounts, childAccountId, hasCreateAccountsAddOn);

  useEffect(() => {
    setHasExportData({
      hasDataToExport: peopleDetailsEntitiesStatus.status === RStatus.Got && !!peopleDetailsEntities.length,
      tooltipContent: "No data available for export. This PDF contains no data.",
    });
  }, [setHasExportData, peopleDetailsEntities, peopleDetailsEntitiesStatus.status]);

  useEffect(() => {
    if (filter.type !== undefined) {
      fetchEngagementPeopleData(pdfId, { ...filter, flowId });
    }
  }, [fetchEngagementPeopleData, pdfId, filter, flowId]);

  return (
    <RequestStatusRenderer state={peopleDetailsEntitiesStatus}>
      {peopleDetailsEntities && (
        <GenericPerformanceList title="People" rows={peopleDetailsEntities} columns={columnOptions} filter={filter} />
      )}
    </RequestStatusRenderer>
  );
};

const mapStateToProps = (state: RootState) => ({
  peopleDetailsEntities: selectEngagementPeopleValue(state),
  peopleDetailsEntitiesStatus: selectEngagementPeopleData(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchEngagementPeopleData: bindAction(fetchEngagementPeopleData, dispatch),
  setHasExportData: bindAction(setHasDataToExport, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(PdfPerformancePeople);
export default ConnectedComponent;
